
import {
  AfterViewChecked,
  Component,
  ElementRef,
  OnInit,
  OnDestroy,
  Renderer2,
} from '@angular/core';
import { BalanceFilterDto } from '@models/balance/balance-filter.dto';
import { BalanceDto } from '@models/balance/balance.dto';
import { TransactionFilterDto } from '@models/balance/transaction-filter.dto';
import { TransactionDto } from '@models/balance/transaction.dto';
import { InOutType } from '@models/generic/in-out-type.enum';
import { Month, MonthString, Months } from '@models/months/month.enum';
import { BalanceService } from '@services/balance.service';
import { MessageService } from '@services/message.service';
import { MenuItem, PrimeIcons } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import { catchError, finalize, throwError } from 'rxjs';

@Component({
  selector: 'ebs-balance',
  templateUrl: './balance.page.html',
  styleUrls: ['./balance.page.scss'],
})
export class BalancePage
  implements OnInit, AfterViewChecked, OnDestroy
{
  public balanceFilter: BalanceFilterDto = {
    months: [],
    years: [],
  };

  public transactionFilter: TransactionFilterDto = {
    months: [],
    years: [],
  };

  public monthShortString = MonthString;

  public months: any[] = Object.keys(Months).map((v: string) => {
    return {
      code: v,
      month: this.getMonth(v),
    };
  });

  public InOutType: typeof InOutType = InOutType;

  filteredMonths: any[] = [];
  searchedMonths: any[] = [];
  filteredYear: any[] = [];

  selectedMonths: any[] | undefined = [];
  selectedMonthsForSearch: any[] | undefined = [];

  balanceStatusList: BalanceDto[] = [];
  selectedBalance: BalanceDto | null = null;

  transactions: TransactionDto[] = [];
  selectedTransaction: TransactionDto | null = null;

  public readonly transactionsPerPage = 5;
  public transactionPage = 0;

  public get paginatedTransactions(): TransactionDto[] {
    const start = this.transactionPage * this.transactionsPerPage;
    return this.transactions.slice(start, start + this.transactionsPerPage);
  }

  public get transactionPageStart(): number {
    return this.transactions.length === 0 ? 0 : this.transactionPage * this.transactionsPerPage + 1;
  }

  public get transactionPageEnd(): number {
    return Math.min((this.transactionPage + 1) * this.transactionsPerPage, this.transactions.length);
  }

  public previousTransactionPage(): void {
    if (this.transactionPage > 0) this.transactionPage--;
  }

  public nextTransactionPage(): void {
    if (this.transactionPageEnd < this.transactions.length) this.transactionPage++;
  }

  // Stato di caricamento separato per le due schede.
  public balancesLoading = false;
  public transactionsLoading = false;
  public transactionsLoaded = false;

  // La prima scheda visualizzata è Resoconti.
  public activeTabIndex = 0;

  // Elementi di caricamento inseriti nei rispettivi riquadri.
  private balanceLoadingOverlay: HTMLElement | null = null;
  private transactionLoadingOverlay: HTMLElement | null = null;

  public items: MenuItem[] = [
    {
      command: () => this.exportToExcel(),
      icon: PrimeIcons.FILE_EXCEL,
    },
  ];

  constructor(
    private balance: BalanceService,
    private messageService: MessageService,
    private elementRef: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {
    this.balanceStatusList = [];
  }

  ngOnInit(): void {
    this.loadAll();
  }

  /**
   * Verifica la presenza degli indicatori dopo il rendering.
   * Questo permette di mostrare lo spinner anche quando
   * la richiesta HTTP parte prima che la pagina sia pronta.
   */
  ngAfterViewChecked(): void {
    this.updateLoadingIndicators();
  }

  ngOnDestroy(): void {
    this.removeBalanceLoadingIndicator();
    this.removeTransactionLoadingIndicator();
  }

  /**
   * Mantiene sincronizzati gli indicatori visivi con
   * balancesLoading e transactionsLoading.
   */
  private updateLoadingIndicators(): void {
    if (this.balancesLoading) {
      this.showBalanceLoadingIndicator();
    } else {
      this.removeBalanceLoadingIndicator();
    }

    if (this.transactionsLoading) {
      this.showTransactionLoadingIndicator();
    } else {
      this.removeTransactionLoadingIndicator();
    }
  }

  /**
   * Crea un indicatore utilizzando la stessa immagine
   * del componente loading-dialog.
   *
   * L'animazione viene applicata direttamente all'immagine,
   * senza aprire una dialog e senza modificare lo SCSS.
   */
  private createLoadingOverlay(
    container: HTMLElement,
  ): HTMLElement {
    const overlay = this.renderer.createElement(
      'div',
    ) as HTMLElement;

    const content = this.renderer.createElement(
      'div',
    ) as HTMLElement;

    const spinner = this.renderer.createElement(
      'img',
    ) as HTMLImageElement;

    const label = this.renderer.createElement(
      'span',
    ) as HTMLElement;

    this.renderer.setAttribute(
      overlay,
      'role',
      'status',
    );

    this.renderer.setAttribute(
      overlay,
      'aria-live',
      'polite',
    );

    this.renderer.setAttribute(
      spinner,
      'src',
      'assets/icons/loading-spinner.svg',
    );

    this.renderer.setAttribute(
      spinner,
      'alt',
      'Caricamento in corso',
    );

    this.renderer.setProperty(
      label,
      'textContent',
      'Caricamento dati in corso...',
    );

    // Il contenitore rimane parte della pagina.
    this.renderer.setStyle(
      container,
      'position',
      'relative',
    );

    // Overlay interno al riquadro, non modale.
    this.renderer.setStyle(
      overlay,
      'position',
      'absolute',
    );

    this.renderer.setStyle(
      overlay,
      'inset',
      '0',
    );

    this.renderer.setStyle(
      overlay,
      'display',
      'flex',
    );

    this.renderer.setStyle(
      overlay,
      'align-items',
      'center',
    );

    this.renderer.setStyle(
      overlay,
      'justify-content',
      'center',
    );

    this.renderer.setStyle(
      overlay,
      'background',
      'rgba(255, 255, 255, 0.88)',
    );

    this.renderer.setStyle(
      overlay,
      'z-index',
      '10',
    );

    this.renderer.setStyle(
      overlay,
      'min-height',
      '180px',
    );

    this.renderer.setStyle(
      overlay,
      'border-radius',
      '12px',
    );

    // Contenuto centrale.
    this.renderer.setStyle(
      content,
      'display',
      'flex',
    );

    this.renderer.setStyle(
      content,
      'flex-direction',
      'column',
    );

    this.renderer.setStyle(
      content,
      'align-items',
      'center',
    );

    this.renderer.setStyle(
      content,
      'justify-content',
      'center',
    );

    this.renderer.setStyle(
      content,
      'gap',
      '16px',
    );

    this.renderer.setStyle(
      content,
      'padding',
      '24px',
    );

    // Dimensioni dell'immagine.
    this.renderer.setStyle(
      spinner,
      'width',
      '88px',
    );

    this.renderer.setStyle(
      spinner,
      'height',
      '88px',
    );

    this.renderer.setStyle(
      spinner,
      'object-fit',
      'contain',
    );

    this.renderer.setStyle(
      spinner,
      'display',
      'block',
    );

    // Testo descrittivo.
    this.renderer.setStyle(
      label,
      'font-size',
      '14px',
    );

    this.renderer.setStyle(
      label,
      'font-weight',
      '500',
    );

    this.renderer.setStyle(
      label,
      'color',
      '#64748b',
    );

    this.renderer.setStyle(
      label,
      'text-align',
      'center',
    );

    this.renderer.appendChild(
      content,
      spinner,
    );

    this.renderer.appendChild(
      content,
      label,
    );

    this.renderer.appendChild(
      overlay,
      content,
    );

    this.renderer.appendChild(
      container,
      overlay,
    );

    // Rotazione continua come nello spinner richiesto.
    spinner.animate(
      [
        { transform: 'rotate(0deg)' },
        { transform: 'rotate(360deg)' },
      ],
      {
        duration: 1400,
        iterations: Infinity,
        easing: 'linear',
      },
    );

    return overlay;
  }

  /**
   * Visualizza lo spinner nella scheda Resoconti.
   */
  private showBalanceLoadingIndicator(): void {
    if (this.balanceLoadingOverlay !== null) {
      return;
    }

    const container =
      this.elementRef.nativeElement.querySelector(
        '.balance-section',
      ) as HTMLElement | null;

    if (container === null) {
      return;
    }

    this.balanceLoadingOverlay =
      this.createLoadingOverlay(container);
  }

  /**
   * Nasconde lo spinner dei Resoconti.
   */
  private removeBalanceLoadingIndicator(): void {
    if (this.balanceLoadingOverlay === null) {
      return;
    }

    const parent = this.balanceLoadingOverlay.parentNode;

    if (parent !== null) {
      this.renderer.removeChild(
        parent,
        this.balanceLoadingOverlay,
      );
    }

    this.balanceLoadingOverlay = null;
  }

  /**
   * Visualizza lo spinner nella scheda Transazioni.
   */
  private showTransactionLoadingIndicator(): void {
    if (this.transactionLoadingOverlay !== null) {
      return;
    }

    const container =
      this.elementRef.nativeElement.querySelector(
        '.balance-transactions-body',
      ) as HTMLElement | null;

    if (container === null) {
      return;
    }

    this.transactionLoadingOverlay =
      this.createLoadingOverlay(container);
  }

  /**
   * Nasconde lo spinner delle Transazioni.
   */
  private removeTransactionLoadingIndicator(): void {
    if (this.transactionLoadingOverlay === null) {
      return;
    }

    const parent =
      this.transactionLoadingOverlay.parentNode;

    if (parent !== null) {
      this.renderer.removeChild(
        parent,
        this.transactionLoadingOverlay,
      );
    }

    this.transactionLoadingOverlay = null;
  }

  /**
   * Aggiorna i resoconti.
   * Le transazioni vengono caricate soltanto dopo che
   * l'utente ha aperto almeno una volta la relativa scheda.
   */
  loadAll(): void {
    const months = (this.selectedMonthsForSearch ?? []).map(
      (item) => item.code,
    );

    this.balanceFilter.months = months;
    this.transactionFilter.months = months;

    this.balancesLoading = true;

    this.balance
      .getBalances(this.balanceFilter)
      .pipe(
        finalize(() => {
          this.balancesLoading = false;
          this.removeBalanceLoadingIndicator();
        }),
      )
      .subscribe({
        next: (result) => {
          this.balanceStatusList = result;
        },
        error: () => {
          this.messageService.error(
            'Resoconti',
            'Errore durante il caricamento dei resoconti',
          );
        },
      });

    // Dopo il primo accesso a Transazioni, loadAll()
    // continua ad aggiornare entrambe le sezioni.
    if (this.transactionsLoaded) {
      this.loadTransactions();
    }
  }

  /**
   * Carica le transazioni soltanto quando necessario.
   */
  public loadTransactions(): void {
    if (this.transactionsLoading) {
      return;
    }

    this.transactionFilter.months = (
      this.selectedMonthsForSearch ?? []
    ).map((item) => item.code);

    this.transactionsLoading = true;

    this.balance
      .getTransactions(this.transactionFilter)
      .pipe(
        finalize(() => {
          this.transactionsLoading = false;
          this.removeTransactionLoadingIndicator();
        }),
      )
      .subscribe({
        next: (result) => {
          // Ordinamento stabile: data più recente, poi ID più alto.
          // La paginazione viene applicata solo dopo questo ordinamento.
          this.transactions = [...result].sort((a, b) => {
            const dateA = this.transactionDateKey(a.transactionDate);
            const dateB = this.transactionDateKey(b.transactionDate);
            if (dateA !== dateB) return dateB.localeCompare(dateA);
            return Number(b.transactionId ?? 0) - Number(a.transactionId ?? 0);
          });
          this.transactionPage = Math.min(
            this.transactionPage,
            Math.max(0, Math.ceil(this.transactions.length / this.transactionsPerPage) - 1),
          );
          this.transactionsLoaded = true;
        },
        error: () => {
          this.messageService.error(
            'Transazioni',
            'Errore durante il caricamento delle transazioni',
          );
        },
      });
  }

  /**
   * Carica le transazioni alla prima apertura della scheda.
   */
  public onTabChange(event: { index: number }): void {
    this.activeTabIndex = event.index;

    if (event.index === 1 && !this.transactionsLoaded) {
      this.loadTransactions();
    }
  }

  createNewBalance(): void {
    const endAmount = this.balanceStatusList[0]
      ? this.balanceStatusList[0].endAmount
      : 0;

    this.balanceStatusList.splice(0, 0, {
      notes: '',
      monthList: '',
      year: new Date().getFullYear().toString(),
      beginAmount: endAmount,
      endAmount: endAmount,
      createdDate: new Date(),
      inNumber: 0,
      outNumber: 0,
      inAmount: 0,
      outAmount: 0,
    });

    this.selectedBalance = this.balanceStatusList[0];
  }

  /** Converte la data in YYYY-MM-DD senza dipendere dal fuso orario. */
  private transactionDateKey(value: Date | string | null | undefined): string {
    if (!value) return '';
    if (typeof value === 'string') {
      const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
      if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
    }
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  }

  /** Campo data nativo sui dispositivi mobili. */
  public get mobileNewTransactionDate(): string {
    return this.transactionDateKey(this.newTransaction?.transactionDate);
  }

  public set mobileNewTransactionDate(value: string) {
    if (!this.newTransaction) return;
    if (!value) {
      this.newTransaction.transactionDate = null as unknown as Date;
      return;
    }
    const parts = value.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => !Number.isFinite(part))) return;
    const date = new Date(parts[0], parts[1] - 1, parts[2]);
    if (date.getFullYear() === parts[0] && date.getMonth() === parts[1] - 1 && date.getDate() === parts[2]) {
      this.newTransaction.transactionDate = date;
    }
  }

  public newTransactionDialogVisible = false;
  public newTransactionSaving = false;
  public newTransaction: TransactionDto | null = null;

  createNewTransaction(): void {
    if (this.newTransactionSaving) return;
    this.newTransaction = {
      amount: null as unknown as number,
      type: InOutType.OUT,
      notes: '',
      category: '',
      createdDate: new Date(),
      modifiedDate: new Date(),
      transactionDate: new Date(),
    };
    this.newTransactionDialogVisible = true;
  }

  public closeNewTransactionDialog(): void {
    if (this.newTransactionSaving) return;
    this.newTransactionDialogVisible = false;
    this.newTransaction = null;
  }

  public saveNewTransaction(): void {
    const transaction = this.newTransaction;
    if (!transaction || this.newTransactionSaving) return;
    if (!Number.isFinite(transaction.amount) || transaction.amount <= 0 || !transaction.transactionDate) {
      this.messageService.error('Nuova transazione', 'Inserisci un importo maggiore di zero e una data valida');
      return;
    }

    if (!transaction.notes || !transaction.notes.trim()) {
      this.messageService.error('Nuova transazione', 'Il campo Note è obbligatorio');
      return;
    }
    transaction.notes = transaction.notes.trim();

    this.newTransactionSaving = true;
    this.balance.createTransaction(transaction)
      .pipe(finalize(() => { this.newTransactionSaving = false; }))
      .subscribe({
        next: () => {
          this.newTransactionDialogVisible = false;
          this.newTransaction = null;
          this.transactionPage = 0;
          this.loadTransactions();
          this.loadAll();
          this.messageService.success('Nuova transazione', 'Transazione registrata con successo');
        },
        error: () => {
          this.messageService.error('Nuova transazione', 'Errore durante il salvataggio della transazione');
        },
      });
  }

  getMonth(month: string): string {
    if (typeof month === 'string') {
      switch (parseInt(month, 10)) {
        case 0:
          return MonthString[Month.JANUARY];
        case 1:
          return MonthString[Month.FEBRUARY];
        case 2:
          return MonthString[Month.MARCH];
        case 3:
          return MonthString[Month.APRIL];
        case 4:
          return MonthString[Month.MAY];
        case 5:
          return MonthString[Month.JUNE];
        case 6:
          return MonthString[Month.JULY];
        case 7:
          return MonthString[Month.AUGUST];
        case 8:
          return MonthString[Month.SEPTEMBER];
        case 9:
          return MonthString[Month.OCTOBER];
        case 10:
          return MonthString[Month.NOVEMBER];
        case 11:
          return MonthString[Month.DECEMBER];
      }
    }

    return '';
  }

  filterMonth(event: AutoCompleteCompleteEvent): void {
    const filtered: any[] = [];
    const query = event.query.toLowerCase();

    for (let i = 0; i < this.months.length; i++) {
      const month = this.months[i];

      if (month.month.toLowerCase().indexOf(query) === 0) {
        filtered.push(month);
      }
    }

    this.filteredMonths = filtered;
  }

  searchMonth(event: AutoCompleteCompleteEvent): void {
    const filtered: any[] = [];
    const query = event.query.toLowerCase();

    for (let i = 0; i < this.months.length; i++) {
      const month = this.months[i];

      if (month.month.toLowerCase().indexOf(query) === 0) {
        filtered.push(month);
      }
    }

    this.searchedMonths = filtered;
  }

  filterYear(event: AutoCompleteCompleteEvent): void {
    this.filteredYear = [event.query];
  }

  deselectBalance(): void {
    if (!this.selectedBalance?.balanceId) {
      this.balanceStatusList.splice(0, 1);
    }

    this.selectedBalance = null;
    this.selectedMonths = [];
  }

  exportToExcel(): void {}

  exportToPdf(): void {}

  removeBalance(balance: BalanceDto): void {
    this.balance
      .removeBalance([balance])
      .pipe(
        catchError((err) => {
          this.deselectBalance();

          this.messageService.error(
            'Rimozione bilancio',
            'Errore durante la rimozione',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.deselectBalance();
        this.loadAll();

        this.messageService.success(
          'Rimozione bilancio',
          'Rimozione avvenuta con successo',
        );
      });
  }

  editBalance(balance: BalanceDto | null): void {
    if (
      balance === null &&
      (this.selectedBalance?.balanceId === undefined ||
        this.selectedBalance?.balanceId === null)
    ) {
      this.balanceStatusList.splice(0, 1);
    }

    this.selectedBalance = balance;

    this.selectedMonths = balance?.monthList
      ?.split(',')
      .map((v: string) => {
        return {
          code: v,
          month: this.getMonth(v),
        };
      });
  }

  modifyBalance(): void {
    if (this.selectedBalance !== null) {
      this.selectedBalance.monthList = this.selectedMonths
        ?.map((item) => item.code)
        .join(',');

      this.saveBalanceChanges(this.selectedBalance);
    }
  }

  saveBalanceChanges(balance: BalanceDto): void {
    const isNew = balance.balanceId === undefined;

    const request = isNew
      ? this.balance.createBalance([balance])
      : this.balance.modifyBalance([balance]);

    request
      .pipe(
        catchError((err) => {
          this.deselectBalance();

          this.messageService.error(
            'Modifica bilancio',
            'Errore durante la modifica',
          );

          this.loadAll();

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.deselectBalance();
        this.loadAll();

        this.messageService.success(
          'Modifica bilancio',
          'Modifica avvenuta con successo',
        );
      });
  }

  modifyTransaction(): void {
    if (this.selectedTransaction !== null) {
      this.saveTransactionChanges(this.selectedTransaction);
    }
  }

  saveTransactionChanges(transaction: TransactionDto): void {
    this.balance
      .createTransaction(transaction)
      .pipe(
        catchError((err) => {
          this.deselectTransaction();
          this.loadAll();

          this.messageService.error(
            'Modifica transazione',
            'Errore durante la modifica',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.deselectTransaction();
        this.loadAll();

        this.messageService.success(
          'Modifica transazione',
          'Modifica avvenuta con successo',
        );
      });
  }

  deselectTransaction(): void {
    this.selectedTransaction = null;
    this.selectedMonths = [];
  }

  removeTransaction(transaction: TransactionDto): void {
    this.balance
      .removeTransaction(transaction)
      .pipe(
        catchError((err) => {
          this.loadAll();
          this.selectedTransaction = null;

          this.messageService.error(
            'Rimozione transazione',
            'Errore durante la rimozione',
          );

          return throwError(() => err);
        }),
      )
      .subscribe(() => {
        this.loadAll();
        this.selectedTransaction = null;

        this.messageService.success(
          'Rimozione transazione',
          'Rimozione avvenuta con successo',
        );
      });
  }

  editTransaction(transaction: TransactionDto | null): void {
    if (
      transaction === null &&
      (this.selectedTransaction?.transactionId === undefined ||
        this.selectedTransaction.transactionId === null)
    ) {
      this.transactions.splice(0, 1);
    }

    this.selectedTransaction = transaction;
  }
}
